import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// ═══════════════════════════════════════════════════════════════════════════════
// RESIZE OBSERVER ERROR SUPPRESSION (AGGRESSIVE)
// ResizeObserver loop errors are harmless browser warnings. We patch ResizeObserver
// itself to prevent these errors from being thrown in the first place.
// ═══════════════════════════════════════════════════════════════════════════════

// Method 1: Patch ResizeObserver to catch errors internally
if (typeof window !== 'undefined') {
  const OriginalResizeObserver = window.ResizeObserver;
  
  if (OriginalResizeObserver) {
    window.ResizeObserver = class PatchedResizeObserver extends OriginalResizeObserver {
      constructor(callback) {
        super((entries, observer) => {
          // Wrap callback in requestAnimationFrame to prevent loop errors
          window.requestAnimationFrame(() => {
            try {
              callback(entries, observer);
            } catch (e) {
              // Silently catch any errors
            }
          });
        });
      }
    };
  }
}

// Method 2: Suppress via window.onerror (backup)
const originalOnError = window.onerror;
window.onerror = function(message, source, lineno, colno, error) {
  if (message && message.toString().includes('ResizeObserver loop')) {
    return true; // Suppress the error
  }
  if (originalOnError) {
    return originalOnError(message, source, lineno, colno, error);
  }
  return false;
};

// Method 3: Suppress via addEventListener with capture (for React overlay)
window.addEventListener('error', function(e) {
  if (e.message && e.message.includes('ResizeObserver loop')) {
    e.stopImmediatePropagation();
    e.stopPropagation();
    e.preventDefault();
    return true;
  }
}, true);

// Method 4: Patch reportError if it exists (React 18+)
if (typeof window.reportError === 'function') {
  const originalReportError = window.reportError;
  window.reportError = function(error) {
    if (error && error.message && error.message.includes('ResizeObserver loop')) {
      return; // Don't report ResizeObserver errors
    }
    return originalReportError.call(this, error);
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// WEBPACK LIVE RELOAD GUARD: Intercept WebSocket messages to prevent reload
// during assessment completion flow. This is more aggressive than module.hot.accept
// because it catches liveReload triggers, not just HMR updates.
// ═══════════════════════════════════════════════════════════════════════════════
(function installWebSocketGuard() {
  // Only in development
  if (process.env.NODE_ENV !== 'development') return;
  
  const OriginalWebSocket = window.WebSocket;
  
  window.WebSocket = function(url, protocols) {
    const ws = new OriginalWebSocket(url, protocols);
    
    // Check if this is a webpack dev server WebSocket
    const isWebpackSocket = url && (
      url.includes('ws://') || 
      url.includes('wss://') ||
      url.includes('/ws') ||
      url.includes('sockjs-node')
    );
    
    if (isWebpackSocket) {
      const originalOnMessage = null;
      
      // Intercept onmessage setter
      let _onmessage = null;
      Object.defineProperty(ws, 'onmessage', {
        get: function() { return _onmessage; },
        set: function(handler) {
          _onmessage = function(event) {
            // Check if completion is in progress
            const isCompletionInProgress = (
              window.__MM_COMPLETION_IN_PROGRESS ||
              sessionStorage.getItem('mm_assessment_completion_in_progress') ||
              window.location.search.includes('success=1')
            );
            
            // Check for completion lock
            let hasCompletionLock = false;
            try {
              for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith('assessment_completion_lock_')) {
                  hasCompletionLock = true;
                  break;
                }
              }
            } catch (e) {}
            
            if (isCompletionInProgress || hasCompletionLock) {
              // Try to parse the message
              try {
                const data = JSON.parse(event.data);
                // Block reload-triggering messages
                if (data.type === 'static-changed' || 
                    data.type === 'content-changed' ||
                    data.type === 'liveReload' ||
                    data.action === 'reload' ||
                    data.action === 'sync') {
                  console.log('[WS_GUARD] Blocked reload message during completion:', data.type || data.action);
                  return; // Don't call the handler
                }
              } catch (e) {
                // Not JSON, check for string messages
                if (typeof event.data === 'string') {
                  if (event.data.includes('reload') || 
                      event.data.includes('static-changed') ||
                      event.data.includes('content-changed')) {
                    console.log('[WS_GUARD] Blocked reload string message during completion');
                    return;
                  }
                }
              }
            }
            
            // Allow the message through
            if (handler) {
              handler.call(ws, event);
            }
          };
        }
      });
      
      // Also intercept addEventListener for 'message' events
      const originalAddEventListener = ws.addEventListener.bind(ws);
      ws.addEventListener = function(type, listener, options) {
        if (type === 'message') {
          const wrappedListener = function(event) {
            const isCompletionInProgress = (
              window.__MM_COMPLETION_IN_PROGRESS ||
              sessionStorage.getItem('mm_assessment_completion_in_progress') ||
              window.location.search.includes('success=1')
            );
            
            let hasCompletionLock = false;
            try {
              for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith('assessment_completion_lock_')) {
                  hasCompletionLock = true;
                  break;
                }
              }
            } catch (e) {}
            
            if (isCompletionInProgress || hasCompletionLock) {
              try {
                const data = JSON.parse(event.data);
                if (data.type === 'static-changed' || 
                    data.type === 'content-changed' ||
                    data.type === 'liveReload' ||
                    data.action === 'reload' ||
                    data.action === 'sync') {
                  console.log('[WS_GUARD] Blocked reload message (addEventListener):', data.type || data.action);
                  return;
                }
              } catch (e) {}
            }
            
            listener.call(ws, event);
          };
          return originalAddEventListener(type, wrappedListener, options);
        }
        return originalAddEventListener(type, listener, options);
      };
    }
    
    return ws;
  };
  
  // Copy static properties
  window.WebSocket.CONNECTING = OriginalWebSocket.CONNECTING;
  window.WebSocket.OPEN = OriginalWebSocket.OPEN;
  window.WebSocket.CLOSING = OriginalWebSocket.CLOSING;
  window.WebSocket.CLOSED = OriginalWebSocket.CLOSED;
  window.WebSocket.prototype = OriginalWebSocket.prototype;
  
  console.log('[WS_GUARD] Installed WebSocket guard for completion flow protection');
})();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<App />);

// ═══════════════════════════════════════════════════════════════════════════════
// WEBPACK HMR GUARD: Prevent hot reload during assessment completion flow
// This is a secondary guard for HMR-specific updates
// ═══════════════════════════════════════════════════════════════════════════════
if (module.hot) {
  // Store the original accept function
  const originalAccept = module.hot.accept;
  
  // Override to check completion flag before accepting updates
  module.hot.accept = function(...args) {
    // Check if completion is in progress
    if (window.__MM_COMPLETION_IN_PROGRESS) {
      console.log('[HMR] BLOCKED - completion in progress');
      return; // Don't accept the update, block the reload
    }
    
    // Also check sessionStorage flag
    try {
      const completionFlag = sessionStorage.getItem('mm_assessment_completion_in_progress');
      if (completionFlag) {
        console.log('[HMR] BLOCKED - completion flag in sessionStorage');
        return;
      }
    } catch (e) {
      // Ignore sessionStorage errors
    }
    
    // Also check URL for success parameter
    if (window.location.search.includes('success=1')) {
      console.log('[HMR] BLOCKED - success=1 in URL');
      return;
    }
    
    // Also check for completion lock in sessionStorage
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('assessment_completion_lock_')) {
          const data = JSON.parse(sessionStorage.getItem(key));
          if (new Date(data.expiresAt) > new Date()) {
            console.log('[HMR] BLOCKED - completion lock exists');
            return;
          }
        }
      }
    } catch (e) {
      // Ignore errors
    }
    
    // No completion in progress, allow the update
    return originalAccept.apply(this, args);
  };
  
  console.log('[HMR] Installed completion guard for hot module replacement');
}
